import { useCallback } from 'react';
import { Form } from 'react-final-form';
import { FormApi } from 'final-form';

import { snackbar } from '@occc-app/design-system';
import { Flow_Types, DirtyFieldsType, Operation } from '@occc-app/offerHelpers/types';
import { OfferFragment, User_Role } from '@occc/graphql';
// eslint-disable-next-line @nx/enforce-module-boundaries
import { getErrorMessage } from '@occc/utils';
import { useConfig } from '@occc/config';
import { useExecuteOperations } from '@occc-app/offerHelpers/operations/useExecuteOperations';
import { useUserSelector } from '@occc-app/App/AppContext';
import { OfferForm } from '@occc-app/offerHelpers/components/OfferForm';

import {
  getLocationInStateConstraintInitialValues,
  getLocationInStateConstraintOperations,
  getStoreLocationOrGroupsConstraintOperations,
  getSubBrandConstraintInitialValues,
  getSubBrandConstraintOperations,
  useGetStoreLocationOrGroupsInitialValues,
} from '@occc/feat-constraints';
import { TargetFormContent } from './TargetFormContent';

import {
  CONFIGURE_PAGE_STATE,
  TargetFormProps,
  TargetFormRenderProps,
  TargetFormValues,
} from '../../types';

type TargetFormContainer = {
  award: OfferFragment;
  refetchAward: () => void;
  setPageState?: (value: CONFIGURE_PAGE_STATE) => void;
  handleCancel: (hasUnsavedChanges?: boolean) => void;
  currentFlowType?: Flow_Types;
};

export const TargetFormContainer = ({
  award,
  refetchAward,
  setPageState,
  handleCancel,
  currentFlowType = Flow_Types.Update,
}: TargetFormContainer) => {
  const currentUser = useUserSelector();
  const { config } = useConfig();

  const isReadOnlyRole = currentUser?.userRole === User_Role.AdminReadOnly;
  const shouldShowSubBrands = !!config.subBrands?.enabled;

  const getTargetFormConstraintOperations = (
    _offerId: string,
    _offer: OfferFragment | undefined,
    values: TargetFormValues,
    dirtyFields: DirtyFieldsType,
  ): Operation[] => [
    ...getLocationInStateConstraintOperations(award.id, award, values, dirtyFields),
    ...getStoreLocationOrGroupsConstraintOperations(
      award.id,
      award,
      values.storeLocationOrGroups,
      dirtyFields,
    ),
    ...getSubBrandConstraintOperations(award.id, award, values, dirtyFields),
  ];
  const [executeOperations, operationsLoading] = useExecuteOperations<TargetFormValues>({
    offerId: award.id,
    offer: award,
    getOperations: getTargetFormConstraintOperations,
  });

  const storeLocationOrGroupsInitialValues = useGetStoreLocationOrGroupsInitialValues(award);
  const subBrandsList = config.subBrands?.list || [];

  const initialValues: TargetFormValues = {
    ...getLocationInStateConstraintInitialValues(award),
    ...storeLocationOrGroupsInitialValues,
    ...getSubBrandConstraintInitialValues(award, subBrandsList),
  };

  const onSubmit = async (values: TargetFormValues, form: FormApi<TargetFormValues>) => {
    try {
      await executeOperations(values, form.getState());
      refetchAward();
      if (setPageState && currentFlowType === Flow_Types.Configure) {
        setPageState(CONFIGURE_PAGE_STATE.DESIGN);
      }
    } catch (error) {
      snackbar.error(getErrorMessage(error));
    }
  };

  const handleNextPageButtonClick = useCallback(() => {
    if (setPageState) {
      setPageState(CONFIGURE_PAGE_STATE.DESIGN);
    }
  }, [setPageState]);

  const targetRenderForm: TargetFormProps['render'] = useCallback(
    ({
      handleSubmit,
      values,
      modifiedSinceLastSubmit,
      dirtySinceLastSubmit,
      dirty,
      submitting,
    }: TargetFormRenderProps) => {
      const isDirty = modifiedSinceLastSubmit ? dirtySinceLastSubmit : dirty;

      return (
        <OfferForm dirty={!submitting && isDirty} handleSubmit={handleSubmit}>
          <TargetFormContent
            values={values}
            isReadOnlyRole={isReadOnlyRole}
            shouldShowSubBrands={shouldShowSubBrands}
            handleCancel={() => handleCancel(isDirty)}
            handleNextButtonClick={handleNextPageButtonClick}
            currentFlowType={currentFlowType}
            submitting={submitting}
            dirty={isDirty}
            isLoading={operationsLoading}
          />
        </OfferForm>
      );
    },
    [
      isReadOnlyRole,
      shouldShowSubBrands,
      handleNextPageButtonClick,
      currentFlowType,
      handleCancel,
      operationsLoading,
    ],
  );

  return (
    <Form<TargetFormValues>
      onSubmit={onSubmit}
      render={targetRenderForm}
      initialValues={initialValues}
      keepDirtyOnReinitialize
    />
  );
};
